import { PrismicRichText } from '@prismicio/react';
import { useEffect, useState } from 'react';
import { sharedLinkResolver } from 'remote-com-shared/domains/navigation/hrefResolver';
import { unlessProp, whenProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import {
  BannerWrapper as BannerWrapperBase,
  BannerIcon,
  BannerCTA,
  PrismicCustomComponents,
} from './Banner';

import { mobileBreakpoint, tabletBreakpoint } from '@/src/domains/shared/breakpoints';

const BannerDescriptionWrapper = styled.div`
  ${tabletBreakpoint(css`
    display: none;
  `)}
`;
const BannerMobileWrapper = styled.div`
  display: none;

  ${tabletBreakpoint(css`
    display: contents;

    ${BannerDescriptionWrapper} {
      display: unset;
    }
  `)}
`;

const BannerImageWrapper = styled.span`
  && {
    margin: -22px 0px -28px 36px;
    min-height: 70px;

    display: flex;
    align-items: center;
    align-self: flex-end;
    ${BannerCTA} {
      display: none;
    }

    ${tabletBreakpoint(css`
      margin: -15px 0px -15px 0px;
      height: auto;
      grid-column: 3/4;

      ${BannerCTA} {
        display: inline-flex;
        margin-left: 15px;
      }

      > img {
        align-self: flex-end;
      }
    `)}
  }
`;

const BannerImage = styled.img`
  height: 60px;
  width: auto;

  ${tabletBreakpoint(css`
    grid-row: 2/3;
  `)}
`;

const BannerWrapper = styled(BannerWrapperBase)`
  && {
    ${tabletBreakpoint(css`
      display: grid;
      grid-template-columns: 1fr max-content min-content;
      gap: 20px;
      padding: 15px 30px;

      * {
        font-size: 15px;
      }

      ${BannerIcon} {
        margin-left: auto;
      }

      ${BannerMobileWrapper} {
        > ${BannerDescriptionWrapper} {
          display: none;

          ${unlessProp(
            '$showPrimaryCTA',
            css`
              display: unset;
            `
          )}
        }
      }
      ${BannerMobileWrapper} > ${BannerCTA} {
        display: none;

        font-weight: 700;
        color: var(--colors-turquoise);
        grid-row: 1/2;
        grid-column: 2/4;
        margin-left: 0;

        ${whenProp(
          '$showPrimaryCTA',
          css`
            display: inline-flex;
          `
        )}
      }

      ${BannerImageWrapper} {
        display: none;

        ${unlessProp(
          '$showPrimaryCTA',
          css`
            display: flex;
          `
        )}
      }
    `)}
    ${mobileBreakpoint(css`
      ${BannerImageWrapper} {
        display: none;
      }
    `)}
  }
`;

function BannerCTAWrapper({ bannerCtaLink, bannerCta, ...props }) {
  switch (bannerCtaLink?.linkType) {
    case 'Link.document': {
      const { target } = bannerCtaLink;

      return (
        <BannerCTA
          label={bannerCta}
          target={target}
          href={sharedLinkResolver(bannerCtaLink)}
          {...props}
        />
      );
    }
    case 'Link.web': {
      const { url, target } = bannerCtaLink;
      return <BannerCTA label={bannerCta} href={url} target={target} {...props} />;
    }
    default:
      return null;
  }
}

export const PaidMarketingBanner = (props) => {
  const {
    bannerIcon,
    description,
    descriptionMobile,
    bannerImage,
    bannerCta,
    bannerCtaMobile,
    bannerCtaLink,
  } = props;

  const [showPrimaryCTA, setShowPrimaryCTA] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowPrimaryCTA((bool) => !bool);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <BannerWrapper $showPrimaryCTA={showPrimaryCTA}>
      {bannerIcon ? <BannerIcon src={bannerIcon.url} alt=" " /> : <span />}
      <BannerDescriptionWrapper>
        <PrismicRichText field={description} components={PrismicCustomComponents} />
      </BannerDescriptionWrapper>
      <BannerMobileWrapper>
        <BannerDescriptionWrapper>
          <PrismicRichText field={descriptionMobile} components={PrismicCustomComponents} />
        </BannerDescriptionWrapper>
      </BannerMobileWrapper>
      {bannerImage ? (
        <BannerImageWrapper>
          <BannerImage src={bannerImage.url} alt={bannerImage.alt} />
          <BannerCTAWrapper label="" bannerCtaLink={bannerCtaLink} chevron />
        </BannerImageWrapper>
      ) : (
        <span />
      )}
      {bannerCta && <BannerCTAWrapper label={bannerCta} bannerCtaLink={bannerCtaLink} chevron />}
      {bannerCtaMobile && (
        <BannerMobileWrapper>
          <BannerCTAWrapper label={bannerCtaMobile} bannerCtaLink={bannerCtaLink} chevron />
        </BannerMobileWrapper>
      )}
    </BannerWrapper>
  );
};
